import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import HoverVideo from "./HoverVideo";
import { VideoMeta } from "../../../services/video.service";

import savedIcon from "../../../assets/MentalPlus/ic_bookmark_on.svg";
import saveOutlineIcon from "../../../assets/MentalPlus/ic_bookmark_off.svg";

const STORAGE_KEY = "savedVideos";

type Props = {
  video: VideoMeta;
  isHover: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onClick: () => void;
  onToggleSaved?: () => void;
  savedOnly?: boolean;
};

const VideoCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      video,
      isHover,
      onHoverStart,
      onHoverEnd,
      onClick,
      onToggleSaved,
      savedOnly,
    },
    ref
  ) => {
    const [isSaved, setIsSaved] = useState(false);

    const syncSavedState = () => {
      const savedVideos = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

      const alreadySaved = savedVideos.some(
        (item: any) => item?.uid === video.uid || item === video.uid
      );

      setIsSaved(alreadySaved);
    };

    useEffect(() => {
      syncSavedState();

      const handleFocus = () => {
        syncSavedState();
      };

      const handleVisibility = () => {
        if (document.visibilityState === "visible") {
          syncSavedState();
        }
      };

      const handleSavedVideosUpdated = () => {
        syncSavedState();
      };

      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("saved-videos-updated", handleSavedVideosUpdated);

      return () => {
        window.removeEventListener("focus", handleFocus);
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("saved-videos-updated", handleSavedVideosUpdated);
      };
    }, [video.uid]);

    const handleToggleSaved = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      const savedVideos = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

      const alreadySaved = savedVideos.some(
        (item: any) => item?.uid === video.uid || item === video.uid
      );

      let updatedVideos;

      if (alreadySaved) {
        updatedVideos = savedVideos.filter(
          (item: any) => item?.uid !== video.uid && item !== video.uid
        );
        setIsSaved(false);
      } else {
        updatedVideos = [...savedVideos, video];
        setIsSaved(true);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedVideos));

      window.dispatchEvent(new Event("saved-videos-updated"));
      onToggleSaved?.();
    };

    return (
      <GridItem
        ref={ref}
        onMouseEnter={() => {
          onHoverStart();
          syncSavedState();
        }}
        onMouseLeave={onHoverEnd}
        onClick={onClick}
      >
        <ThumbContainer>
          <ThumbImg src={video.thumbnailUrl} alt="video thumbnail" />
          <HoverVideo hlsUrl={video.hlsUrl} isHover={isHover} />

          <SaveButton
            type="button"
            onClick={handleToggleSaved}
            aria-label={
              isSaved || savedOnly
                ? "Retirer des vidéos enregistrées"
                : "Enregistrer la vidéo"
            }
          >
            <SaveImg
              src={isSaved || savedOnly ? savedIcon : saveOutlineIcon}
              alt={isSaved || savedOnly ? "video enregistrée" : "enregistrer"}
            />
          </SaveButton>
        </ThumbContainer>

        <TitleSection>
          <VideoTitle>{video.meta?.title || video.meta?.name || "Sans titre"}</VideoTitle>
        </TitleSection>
      </GridItem>
    );
  }
);

VideoCard.displayName = "VideoCard";

export default VideoCard;

const ThumbContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
  background: black;
  overflow: hidden;
`;

const GridItem = styled.div`
  background: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.06);
  overflow: hidden;
  border-radius: 18px;
  width: 100%;
  aspect-ratio: 166 / 200;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
`;

const TitleSection = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 6px 8px;
  height: 44px;
  background: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.06);

  @media (min-width: 768px) {
    padding: 8px 10px;
    height: 48px;
  }
`;

const VideoTitle = styled.p`
  margin: 0;
  color: var(--app-color-text, #17243c);
  font-size: clamp(12px, 1.5vw, 15px);
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const SaveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 20;

  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const SaveImg = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5));
`;